import '../../common/rotaStyles.css'
import { theme } from '@lmc/ui-kit'
import { print } from '@mobiscroll/print'
import {
  Eventcalendar,
  type MbscCalendarDayData,
  type MbscCalendarEventData
} from '@mobiscroll/react'
import { Button, message } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { DAILY, USER, VISIT } from 'screens/Rostering/constants'
import {
  ALLOCATION_LIST_QUERY_KEY,
  INSTANCE_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import useAllocationDoubleBookings from 'screens/Rostering/hooks/useAllocationDoubleBookings'
import useCheckGeneratedRota from 'screens/Rostering/hooks/useCheckGeneratedRota'
import type { CountChangesResponse } from 'screens/Rostering/hooks/useCountChanges'
import { useDrawerData } from 'screens/Rostering/hooks/useDrawerData'
import useRevisionRollback from 'screens/Rostering/hooks/useRevisionRollback'
import useRevisionUpdate from 'screens/Rostering/hooks/useRevisionUpdate'
import { UNASSIGNED_ID } from 'screens/Rostering/hooks/utils'
import { RotaDrawer } from 'screens/Rostering/RotaDrawer'
import NewRotaPatternModal from 'screens/Rostering/RotaPattern/NewRotaPatternModal/NewRotaPatternModal'
import {
  type RotaBuilderFiltersType,
  RotaInstanceStatus,
  type RotaUserEventInstance,
  type START_OVERLAY_TYPE,
  type UserResource
} from 'screens/Rostering/types'
import {
  BACKEND_REQUEST_STRING_FORMAT,
  getDateWithoutUTC,
  YEAR_FIRST_DATE_FORMAT
} from 'screens/Rostering/utils.datetime'
import { commonPatterns } from 'utils/enhancedTestIds'
import { RotaBuilderFilters } from '../../common'
import {
  RotaBuilderContentWrapper,
  RotaWrapper
} from '../../common/components/commonStyledComponents'
import Day from '../../common/components/eventcalendar/Day'
import Event from '../../common/components/eventcalendar/Event'
import EventCalendarHeader from '../../common/components/eventcalendar/EventcalendarHeader'
import Hour from '../../common/components/eventcalendar/Hour'
import Resource from '../../common/components/eventcalendar/Resource'
import { getWarningsListForEvent } from '../../common/components/eventcalendar/utils'
import StartRotaPublishModal from '../../common/components/modals/StartRotaPublishModal'
import PublishedRotaOverlay from '../../common/components/PublishedRotaOverlay'
import StartRotaOverlay from '../../common/components/StartRotaOverlay'
import UndoButton from '../../common/components/UndoButton'
import { CalendarOptions } from '../../common/constants'
import DoubleBookedModal from '../AllocationWindow/components/DoubleBookedModal'

dayjs.extend(isoWeek)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const MY_MODULES = [print]

type UserRotaProps = {
  testId?: string
  selectedDate: Date
  setSelectedDate: any
  timelineView: any
  setTimelineView: (view: any) => void
  resources: UserResource[]
  events: RotaUserEventInstance[]
  isLoading: boolean
  publishedRota?: boolean
  invalid: any
  filterParams?: RotaBuilderFiltersType
  onFilterChange?: (value: RotaBuilderFiltersType) => void
  isDailyView: boolean
  refetchCountChanges?: () => void
  countChangesData: CountChangesResponse
  metadata: {
    unassignedHours: number
    selectedDate?: Date
    isDailyView?: boolean
  }
  overlayType?: START_OVERLAY_TYPE
  hasFilters?: boolean
  calendarOptions?: typeof CalendarOptions
  showAllEvents?: boolean
  onToggleShowAllEvents?: () => void
}

const UserRota = ({
  testId,
  selectedDate,
  setSelectedDate,
  timelineView,
  setTimelineView,
  resources,
  events,
  isLoading,
  publishedRota,
  invalid,
  filterParams,
  onFilterChange,
  isDailyView,
  refetchCountChanges,
  countChangesData,
  metadata,
  overlayType,
  hasFilters,
  calendarOptions,
  showAllEvents,
  onToggleShowAllEvents
}: UserRotaProps) => {
  const { t } = useTranslation()
  const baseId = testId || 'user-rota'

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'new_create_pattern',
      'rostering_builder_drag_and_drop',
      'rota_warnings_email_notifications'
    ]
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  const flagsNewCreatePatternEnabled = flags?.new_create_pattern?.enabled

  const flagsDragAndDropEnabled =
    flags?.rostering_builder_drag_and_drop?.enabled

  const [isZoomed, setIsZoomed] = useState(false)

  const [resourceToRemove, setResourceToRemove] = useState<
    string[] | undefined
  >(undefined)

  const [inst, setInst] = useState<Eventcalendar | null>(null)

  const [showDoubleBookedPrompt, setShowDoubleBookedPrompt] = useState(false)
  const [doubleBook, setDoubleBook] = useState(false)

  const [doubleBookHandler, setDoubleBookHandler] = useState<() => void>(
    () => () => {}
  )

  const [currentDragEvent, setCurrentDragEvent] = useState<any>(null)

  const [lastDraggedEventId, setLastDraggedEventId] = useState<string | null>(
    null
  )

  const printView = useCallback(() => {
    inst!.print()
  }, [inst])

  // Create view object with dynamic calendar options
  const view = useMemo(() => {
    const currentCalendarOptions = calendarOptions || CalendarOptions
    let dynamicTimelineView = timelineView

    // Override timelineView with our dynamic calendar options
    if (timelineView?.type === 'week') {
      dynamicTimelineView = currentCalendarOptions.weekly
    } else if (timelineView?.type === 'day') {
      if (isZoomed) {
        dynamicTimelineView = currentCalendarOptions.dailyZoomed
      } else {
        dynamicTimelineView = currentCalendarOptions.daily
      }
    }

    return { timeline: dynamicTimelineView }
  }, [timelineView, calendarOptions, isZoomed])

  const queryClient = useQueryClient()

  const {
    drawerVisible,
    setDrawerVisible,
    drawerAllocatedTeamVisible,
    setDrawerAllocatedTeamVisible,
    setDrawerDrawerOriginalDataId,
    drawerOriginalDataId
  } = useDrawerData()

  const { updateRevision } = useRevisionUpdate({
    onUpdateSuccess: () => {
      refetchCountChanges?.()
      invalidateQueries()
    }
  })

  const { rollbackRevision } = useRevisionRollback({
    id: lastDraggedEventId || undefined
  })

  const handleUndoWithEventId = async (eventId: string) => {
    // Set the event ID and then use the existing rollback function
    setLastDraggedEventId(eventId)

    // Small delay to let the hook update
    setTimeout(async () => {
      if (!rollbackRevision) return

      try {
        // Hide all existing toasts first
        message.destroy()

        await rollbackRevision()

        setLastDraggedEventId(null)
        showToast({
          title: t('CHANGE_REVERTED_SUCCESSFULLY'),
          type: 'success'
        })
      } catch (error) {
        showToast({
          title: t('FAILED_TO_UNDO_CHANGE'),
          type: 'error'
        })
      }
    }, 100)
  }

  const { doubleBookings } = useAllocationDoubleBookings()

  const invalidateQueries = () => {
    queryClient?.invalidateQueries([INSTANCES_QUERY_KEY])
    queryClient?.invalidateQueries([INSTANCE_QUERY_KEY])
    queryClient?.invalidateQueries([REVISION_QUERY_KEY])
    queryClient?.invalidateQueries([REVISIONS_QUERY_KEY])
    queryClient?.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
  }

  const onCalendarChange = (event: any) => {
    const val = event.target.value
    if (val === 'weekly') {
      setTimelineView(CalendarOptions.weekly)
    } else if (val === DAILY) {
      setTimelineView(CalendarOptions.daily)
      if (dayjs(selectedDate).isSame(dayjs(), 'isoWeek')) {
        setSelectedDate(new Date())
      }
    }
    return null
  }

  const handleZoom = () => {
    setIsZoomed(!isZoomed)
    const newView = isZoomed
      ? CalendarOptions.daily
      : CalendarOptions.dailyZoomed
    setTimelineView(newView)
  }

  const renderResource = useCallback(
    (resource: UserResource) => {
      const enhancedMetadata = {
        ...metadata,
        date: isDailyView
          ? selectedDate
          : dayjs(selectedDate).startOf('isoWeek').toDate(),
        isDailyView
      }
      return (
        <Resource metadata={enhancedMetadata} resource={resource} type={USER} />
      )
    },
    [metadata, selectedDate, isDailyView]
  )

  const renderScheduleEvent = useCallback(
    (args: MbscCalendarEventData) => (
      <Event
        // @ts-ignore
        args={args}
        type={USER}
        instanceWarnings={countChangesData?.warnings?.instanceWarningMaps}
        warnings={getWarningsListForEvent(
          countChangesData?.warnings?.instanceWarningMaps,
          args?.original?.id as unknown as string,
          args?.original?.teamMembers,
          t,
          flagsRosteringParkedFeaturesEnabled,
          args?.original?.status,
          flagsRotaWarningsEmailNotifications
        )}
        setDrawerAllocatedTeamVisible={(flag) => {
          setDrawerDrawerOriginalDataId(args?.id)
          setDrawerAllocatedTeamVisible(flag)
        }}
        setDrawerVisible={(flag) => {
          setDrawerDrawerOriginalDataId(args?.id)
          setDrawerVisible(flag)
        }}
        isDailyView={isDailyView}
        publishedRota={publishedRota}
        testId={`${baseId}-event-${args?.original?.id}`}
      />
    ),
    [
      countChangesData?.warnings,
      events,
      isDailyView,
      publishedRota,
      baseId,
      flagsRosteringParkedFeaturesEnabled,
      flagsRotaWarningsEmailNotifications
    ]
  )

  const renderHour = (args: MbscCalendarDayData) => {
    return <Hour date={args.date} />
  }

  const renderDay = (args: MbscCalendarDayData) => {
    return <Day date={args.date} isDailyView={isDailyView} />
  }

  const renderHeader = useCallback(() => {
    return (
      <EventCalendarHeader
        selectedDate={selectedDate}
        onCalendarChange={onCalendarChange}
        handleZoom={handleZoom}
        isZoomed={isZoomed}
        isDailyView={isDailyView}
        countChangesData={countChangesData}
        publishedRota={publishedRota}
        filterParams={filterParams}
        printView={printView}
        showAllEvents={showAllEvents}
        onToggleShowAllEvents={onToggleShowAllEvents}
      />
    )
  }, [
    selectedDate,
    onCalendarChange,
    handleZoom,
    timelineView,
    isZoomed,
    isDailyView,
    countChangesData,
    publishedRota,
    filterParams
  ])

  const allResources = useMemo(() => {
    const unassignedRow = {
      id: UNASSIGNED_ID,
      name: 'Unassigned',
      background: theme.color.neutral.color9,
      fixed: !showAllEvents // Make non-sticky when showing all events
    }

    return [unassignedRow, ...resources]
  }, [showAllEvents, resources])

  const [originalEventPosition, setOriginalEventPosition] = useState<{
    start: string
    end: string
    resource: string[]
  } | null>(null)

  const updateEvent = async (
    event: RotaUserEventInstance,
    addIds: string[],
    removeIds: string[],
    teamMembersDoubleBooked: string[],
    allowDoubleBooking: boolean,
    hasTimingChanged: boolean = false,
    originalEventData?: {
      start: string | Date
      end: string | Date
      resource: string[]
    }
  ) => {
    // If no team member changes AND no timing changes, do nothing
    if (addIds.length === 0 && removeIds.length === 0 && !hasTimingChanged) {
      return true
    }

    const updatePayload: any = {
      eventId: event.id || '',
      startDate: getDateWithoutUTC(event?.start, BACKEND_REQUEST_STRING_FORMAT),
      endDate: getDateWithoutUTC(event?.end, BACKEND_REQUEST_STRING_FORMAT)
    }

    // Add teamMembers if there are team member changes OR if there are double bookings to report
    if (
      addIds.length > 0 ||
      removeIds.length > 0 ||
      teamMembersDoubleBooked.length > 0
    ) {
      updatePayload.teamMembers = {
        add: addIds?.map((id) => ({ teamMemberId: id, role: 'carer' })),
        remove: removeIds,
        doubleBooked: teamMembersDoubleBooked,
        allowDoubleBooking
      }
    }

    // Store event ID for undo (only for drag operations and when feature flag is enabled)
    if (originalEventData && flagsDragAndDropEnabled) {
      const eventId = event.id || ''
      setLastDraggedEventId(eventId)

      // Add undo action to payload for drag operations - capture eventId directly
      updatePayload.undoAction = (
        <UndoButton onClick={() => handleUndoWithEventId(eventId)} />
      )
    }

    await updateRevision?.(updatePayload)

    return true
  }

  const handleDragEventStart = async (e: any) => {
    const resourceToRemove = e?.event?.resource?.filter(
      (res: string) => res !== UNASSIGNED_ID
    )

    if (resourceToRemove?.length) {
      setResourceToRemove(resourceToRemove)
    }

    setOriginalEventPosition({
      start: e.event.start,
      end: e.event.end,
      resource: e.event.resource
    })
  }

  const handleDragEventEnd = async (e: any): Promise<boolean> => {
    const isInvalidAbsenceDrop = invalid?.some((invalidArea: any) => {
      const eventStart = dayjs(e.event.start)
      const eventEnd = dayjs(e.event.end)
      const invalidStart = dayjs(invalidArea.start)
      const invalidEnd = dayjs(invalidArea.end)

      return (
        eventStart.isBefore(invalidEnd) &&
        eventEnd.isAfter(invalidStart) &&
        e.resource === invalidArea.resource
      )
    })

    if (isInvalidAbsenceDrop) {
      showToast({
        title: t('DRAG_AND_DROP_ABSENCE_ERROR'),
        type: 'error'
      })

      if (originalEventPosition) {
        e.event.start = originalEventPosition.start
        e.event.end = originalEventPosition.end
        e.event.resource = originalEventPosition.resource
      }
      setOriginalEventPosition(null)
      return false
    }

    // Check for restricted event statuses (matching drawer restrictions)
    const isInProgress =
      e.event.status === RotaInstanceStatus.IN_PROGRESS ||
      e.event.clockIns?.length > 0
    const isDone = e.event.status === RotaInstanceStatus.DONE
    const isOverdue = e.event.status === RotaInstanceStatus.OVERDUE
    const isCancelled = e.event.status === RotaInstanceStatus.CANCELLED

    const isInvalidStatusDrop =
      isInProgress || isDone || isOverdue || isCancelled

    if (isInvalidStatusDrop) {
      let errorMessage = t('CANNOT_EDIT_ALLOCATION_STATUS')

      if (isInProgress) {
        errorMessage = t('CANNOT_EDIT_IN_PROGRESS_EVENT')
      } else if (isOverdue) {
        errorMessage = t('CANNOT_EDIT_OVERDUE_EVENT')
      }

      showToast({
        title: errorMessage,
        type: 'error'
      })

      if (originalEventPosition) {
        e.event.start = originalEventPosition.start
        e.event.end = originalEventPosition.end
        e.event.resource = originalEventPosition.resource
      }
      setOriginalEventPosition(null)
      return false
    }

    const hasTimingChanged = originalEventPosition
      ? originalEventPosition.start !== e.event.start ||
        originalEventPosition.end !== e.event.end
      : false

    setOriginalEventPosition(null)

    const currentIds = e?.event?.resource?.filter(
      (res: string) => res !== UNASSIGNED_ID
    ) as string[]

    const teamMemberIds = e?.event?.teamMembers.map(
      (member: { teamMemberId: string }) => member.teamMemberId
    )

    const addIds = currentIds.filter((id) => !teamMemberIds.includes(id))

    const validAddIds = addIds.filter((id) => {
      const resource = allResources.find((r) => r.id === id)
      if (resource && 'user' in resource) {
        return (
          resource.user?.active !== false && resource.user?.deleted !== true
        )
      }
      return true
    })

    const removeIds = resourceToRemove?.filter(
      (res: string) => !currentIds?.includes(res)
    )

    // Determine which team members to check for double bookings
    // If adding new team members, check those. If only changing time, check existing team members.
    const teamMembersToCheck =
      validAddIds.length > 0
        ? validAddIds
        : hasTimingChanged && teamMemberIds.length > 0
          ? teamMemberIds
          : []

    let teamMembersDoubleBooked: string[] = []

    if (teamMembersToCheck.length > 0) {
      try {
        const result = await doubleBookings({
          id: e.event.id,
          teamMembers: teamMembersToCheck,
          startTime: hasTimingChanged
            ? getDateWithoutUTC(e.event.start, BACKEND_REQUEST_STRING_FORMAT)
            : undefined,
          endTime: hasTimingChanged
            ? getDateWithoutUTC(e.event.end, BACKEND_REQUEST_STRING_FORMAT)
            : undefined
        })
        // Ensure result is an array
        teamMembersDoubleBooked = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('Error checking double bookings:', error)
        teamMembersDoubleBooked = []
      }
    }

    if (
      (validAddIds.length > 0 ||
        (removeIds || []).length > 0 ||
        hasTimingChanged) &&
      !showDoubleBookedPrompt &&
      teamMembersDoubleBooked?.length > 0
    ) {
      setShowDoubleBookedPrompt(true)
      setCurrentDragEvent(e.event)

      return new Promise((resolve) => {
        const handleModalConfirm = async (allowDoubleBooking: boolean) => {
          const result = await updateEvent(
            e.event,
            addIds,
            removeIds || [],
            teamMembersDoubleBooked,
            allowDoubleBooking,
            hasTimingChanged,
            originalEventPosition || undefined
          )
          setShowDoubleBookedPrompt(false)
          setResourceToRemove(undefined)
          setCurrentDragEvent(null)
          resolve(result)
        }

        setDoubleBookHandler(() => handleModalConfirm)
      })
    }

    await updateEvent(
      e.event,
      validAddIds,
      removeIds || [],
      teamMembersDoubleBooked,
      true,
      hasTimingChanged,
      originalEventPosition || undefined
    )
    setResourceToRemove(undefined)
    return true
  }

  const handleSelectedDateChange = useCallback((args: any) => {
    setSelectedDate(args.date)
  }, [])

  const { data: hasRotaData, isFetched: isRotaFetched } = useCheckGeneratedRota(
    { selectedDate }
  )

  const [isInitialized, setIsInitialized] = useState(false)
  const [hasEvents, setHasEvents] = useState(false)

  const [showStartRotaModal, setShowStartRotaModal] = useState(false)

  const openStartRotaModal = () => {
    setShowStartRotaModal(true)
  }

  const closeStartRotaModal = () => {
    setShowStartRotaModal(false)
  }

  useEffect(() => {
    if (isRotaFetched) {
      setHasEvents(events.length > 0 || !!hasRotaData)
      setIsInitialized(true)
    }
  }, [selectedDate, hasRotaData, isRotaFetched, events])

  const [clickedDate, setClickedDate] = useState<Date | null>(null)
  const [clickedUserId, setClickedUserId] = useState<string | null>(null)
  const [clickedTimeStart, setClickedTimeStart] = useState<string | null>(null)
  const [clickedTimeEnd, setClickedTimeEnd] = useState<string | null>(null)
  const [showPatternModal, setShowPatternModal] = useState(false)

  const handleCellClick = (event: any) => {
    const date = new Date(event.date)
    setClickedDate(date)

    if (event.resource) {
      setClickedUserId(event.resource)

      if (isDailyView) {
        const clickedHour = date.getHours()
        const clickedMinute = date.getMinutes()

        const startTime = `${String(clickedHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

        const endHour = (clickedHour + 1) % 24
        const endTime = `${String(endHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

        setClickedTimeStart(startTime)
        setClickedTimeEnd(endTime)
      } else {
        setClickedTimeStart(null)
        setClickedTimeEnd(null)
      }

      setShowPatternModal(true)
    }
  }

  const getInitialValues = () => {
    const values: any = {
      reoccurenceTooglePattern: false
    }

    if (clickedDate) {
      values.patternStart = dayjs(clickedDate)
    }

    if (clickedUserId) {
      values.teamMembers = [clickedUserId]
      values.numberSupply = 1
    }

    if (isDailyView && clickedTimeStart && clickedTimeEnd) {
      values.startTime = clickedTimeStart
      values.endTime = clickedTimeEnd
    }

    return values
  }

  const handleDoubleBookedModalCancel = () => {
    if (originalEventPosition && currentDragEvent) {
      currentDragEvent.start = originalEventPosition.start
      currentDragEvent.end = originalEventPosition.end
      currentDragEvent.resource = originalEventPosition.resource
    }

    setOriginalEventPosition(null)
    setResourceToRemove(undefined)
    setShowDoubleBookedPrompt(false)
    setCurrentDragEvent(null)

    invalidateQueries()
  }

  return (
    <RotaWrapper data-testid={baseId}>
      <RotaBuilderFilters
        testId={`${baseId}-filters`}
        builderType={USER}
        value={filterParams}
        onChange={onFilterChange}
        publishedRota={publishedRota}
      />
      <RotaBuilderContentWrapper data-testid={`${baseId}-content`}>
        <Eventcalendar
          data-testid={`${baseId}-calendar`}
          firstDay={1}
          timeFormat="HH:mm"
          ref={setInst}
          modules={MY_MODULES}
          className="eventcalendar"
          dragToMove={!publishedRota}
          eventDelete={!publishedRota}
          dragInTime={!publishedRota && isDailyView && flagsDragAndDropEnabled}
          clickToCreate={false}
          dragToCreate={false}
          dragToResize={
            !publishedRota && isDailyView && flagsDragAndDropEnabled
          }
          showToday={false}
          showEventTooltip={false}
          view={view}
          data={events}
          resources={allResources}
          invalid={invalid}
          renderDay={renderDay}
          renderHour={renderHour}
          renderScheduleEvent={renderScheduleEvent}
          renderEvent={renderScheduleEvent}
          renderResource={renderResource}
          renderHeader={renderHeader}
          onEventDragEnd={handleDragEventEnd}
          onEventDragStart={handleDragEventStart}
          selectedDate={selectedDate}
          onSelectedDateChange={handleSelectedDateChange}
          onCellClick={
            flagsNewCreatePatternEnabled && !publishedRota
              ? handleCellClick
              : undefined
          }
        />
        {!publishedRota && isInitialized && (
          <StartRotaOverlay
            data-testid={`${baseId}-start-overlay`}
            hasEvents={hasEvents}
            isLoading={isLoading}
            onStartRota={openStartRotaModal}
            type={overlayType}
          />
        )}
        {publishedRota && events.length === 0 && hasFilters && (
          <PublishedRotaOverlay
            data-testid={`${baseId}-published-overlay`}
            isLoading={isLoading}
          />
        )}
      </RotaBuilderContentWrapper>
      {showStartRotaModal && (
        <StartRotaPublishModal
          data-testid={commonPatterns.modal(baseId, 'start-rota')}
          open={showStartRotaModal}
          onClose={closeStartRotaModal}
          fromDate={dayjs(selectedDate)
            .startOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
          toDate={dayjs(selectedDate)
            .endOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
        />
      )}
      {drawerVisible && (
        <RotaDrawer
          data-testid={`${baseId}-drawer`}
          onClose={() => {
            setDrawerVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerVisible}
          id={drawerOriginalDataId}
          publishedRota={publishedRota}
          instanceWarningMaps={countChangesData?.warnings?.instanceWarningMaps}
        />
      )}
      {drawerAllocatedTeamVisible && (
        <RotaDrawer
          data-testid={`${baseId}-allocated-team-drawer`}
          onClose={() => {
            setDrawerAllocatedTeamVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerAllocatedTeamVisible}
          id={drawerOriginalDataId}
          defaultTab={2}
          publishedRota={publishedRota}
          instanceWarningMaps={countChangesData?.warnings?.instanceWarningMaps}
        />
      )}
      {showDoubleBookedPrompt && (
        <DoubleBookedModal
          data-testid={commonPatterns.modal(baseId, 'double-booked')}
          showPrompt={showDoubleBookedPrompt}
          setShowPrompt={setShowDoubleBookedPrompt}
          confirmNavigation={doubleBookHandler}
          isDoubleBookedSelected={doubleBook}
          setIsDoubleBookedSelected={setDoubleBook}
          cancelNavigation={handleDoubleBookedModalCancel}
        />
      )}
      {showPatternModal && (
        <NewRotaPatternModal
          testId={commonPatterns.modal(baseId, 'new-pattern')}
          onCancel={() => setShowPatternModal(false)}
          type={VISIT}
          initialValues={getInitialValues()}
        />
      )}
    </RotaWrapper>
  )
}

export default UserRota
